import React from 'react';
import moment from 'moment-timezone';
import { CurrencyAmount, FormatCurrencyValue, formatCurrencyValue } from '../../../utils/currency';
import { LineItem } from '../../LineItems/LineItem';
import LineItemDisplay from '../../LineItems/LineItemDisplay';
import { clearCart, removeFromCart, updateToCart } from '../../../utils/kit/modules/shoppingCart';
import GACardInput, { CardInputState } from '../../inputs/GACardInput';
import { CardInput } from '../../../utils/kit/modules/cardDetails';
import { GACntxt } from "../../../AllianceApp";
import PaymentProcessingWidget from '../../ProcessingIndicators/PaymentProcessingWidget';
import CheckoutCompleted from '../CheckoutCompleted/CheckoutCompleted'
import { ForSaleConfiguration, ForSaleData, LineItemTypes, ShirtData } from '../../LineItems/LineItemTypes/LineItemTypes';
import { deleteCartId, reserveItemFromInventory } from '../../../functions/Items';
import { clearCheckoutEvent, getCheckoutEvent } from '../../../functions/CheckoutEvent';
import usePaymentProcessing from '../../../hooks/usePaymentProcessing';
import useIntervalTimer, { IntervalTimerStatus } from '../../../hooks/useIntervalTimer';
/**
 * Renders the checkout page for a given set of line items for a customer.
 */

/** 
 * Props for Checkout Page Display
 * lineItems: List of Line Items to display
 */
export interface checkoutPageProps {
    lineItems: LineItem[]
}

/** 
 * Verify that all items in array are unique
 * TODO: Move into Utils
 */
function areAllValuesUnique<T>(array: T[]): boolean {
    //TODO: There's got to be a better way than this...
    const uniqueItemsSet = new Set(array);
    return uniqueItemsSet.size === array.length;
}

/**
 * Internal interface for managing the validity of certain parts of lineItems.
 */
interface validityMapList 
{
    priceValid: boolean,
    dataValid: boolean,
    priceErrMsg?: string,
    dataErrMsg?: string
}

const Checkout = (props: checkoutPageProps) => {

    /**
     * Construct a map used to manage the changes in line item prices
     * @param lineItems Line Items to manage
     * @returns Record object maping line item names to their default prices if valid, {} if not.
     */
    const constructDefaultPriceMap = ( lineItems: LineItem[]) => {

        return lineItems.reduce((priceMap, lineItem) => 
        {
            return({...priceMap,  [lineItem.itemConfiguration.dbId] : lineItem.unitPriceConfiguration.defaultPrice});
        },{});
    }

    /**
     * Construct a map used to manage the changes in line item data
     * @param lineItems Line Items to manage
     * @returns Record object maping line item names to, initially, a null input if valid, {} if not.
     */
    const constructDataMap = (lineItems: LineItem[]) =>
    {
        return lineItems.reduce((dataMap, lineItem) => 
        {
            return {...dataMap, 
                [lineItem.itemConfiguration.dbId] : 
                lineItem.itemFormat === LineItemTypes.Shirts || LineItemTypes.ForSale ? (lineItem.data) : {}}
        },{});
    }

    /**
     * Construct a map used to manage the changes in the validity of line item prices and data
     * @param lineItems Line Items to manage
     * @returns Record object maping line item names to a validityMapList with default values, {} if not.
     */
    const constructValidityMap = (lineItems: LineItem[]) =>
    {
        return lineItems.reduce((dataMap, lineItem) => 
        {
            return({...dataMap,  [lineItem.itemConfiguration.dbId] : {
                priceValid: true,
                dataValid: true
            }})
        },{});
    }

    /* Callback trigger when an item with a reservation time in the cart expires
    For now, this just clears the cart in its entirety */
    const onReservationExpiration = async () => 
    {
        if(props.lineItems && props.lineItems.length !== 0)
        {
            props.lineItems.map(async (lineItem) => 
            {
                await reserveItemFromInventory(lineItem.itemConfiguration.dbId, userContext.userTGAId ?? 0, 0);
                removeFromCart(lineItem.itemConfiguration.dbId);
            });
        }
    }

    const userContext = React.useContext(GACntxt);

    const {startTimer: startReservationTimer, stopTimer: stopReservationTimer, timerStatus: timerReservationStatus, formattedTimeRemaining: formattedReservationTimeRemaining} = useIntervalTimer(false, onReservationExpiration);

    const {processingError, payRequestProcessing, completedOrderId, completedOrder, checkoutComplete, processingDelay, extendedProcessingMessage,  processPayment} = usePaymentProcessing(userContext.userTGAId ?? 0, userContext.userAccessToken ?? "");

    //TODO: Despite having the same keys, this map is repeated 3 times to optimize rendering
    //(So we don't spend time rendering a price change if only the data has changed, etc.)
    //It's possible that adding the validity as part of the price & data maps may work better,
    //but would require more to be done by internal components. 
    const [priceMap, setPriceMap] = React.useState<Record<number, CurrencyAmount>>(props.lineItems ? constructDefaultPriceMap(props.lineItems) : {});
    //TODO: Create a flexible-enough JSON type that we can use here.
    const [dataMap, setDataMap] = React.useState<Record<number, any>>(props.lineItems ? constructDataMap(props.lineItems) : {});
    //TODO: Validities currently don't provide ways to send an custom error message. That might be helpful here.
    const [validityMap, setValidityMap] = React.useState<Record<number, validityMapList>>(props.lineItems ? constructValidityMap(props.lineItems) : {});
    const [validationErrorArray, setValidationErrorArray] = React.useState<string[]>([]);

    const [cardValid, setCardValid] = React.useState<boolean>(false);
    const [cardData, setCardData] = React.useState<CardInput>();
    const [cardErrors, setCardErrors] = React.useState<CardInputState>();
    const [checkoutEventIds, setCheckoutEventIds] = React.useState<number[]>([]);

    const getEventIdForItemId = async (id: number) =>
    {
        return getCheckoutEvent();
    }

    React.useEffect(() => 
    {
        let isMounted = true;
        const asyncFunction = async () =>
        {
            if(props.lineItems)
            {
                const eventIds = await Promise.all(props.lineItems.map(async (value) => 
                {
                    const id = value.itemConfiguration.dbId;
                    return await getEventIdForItemId(id);
                }));

                const uniqueEventIds = [...new Set(eventIds)];

                isMounted && setCheckoutEventIds(uniqueEventIds);
            }
        }

        asyncFunction();

        return(() => {isMounted = false})
    },[props.lineItems])

    /* useEffect to determine whether some items in the cart have a reservation time, and
    calculating the time before they expire. */
    React.useEffect(() =>
    {
        const latestReservationTime = props.lineItems.reduce((timeStamp, item) =>
        {
            if(item.reservation_time === undefined)
            {
                return timeStamp;
            }
            else if(timeStamp === "")
            {
                return item.reservation_time;
            }
            else
            {
                return Date.parse(item.reservation_time) > Date.parse(timeStamp) ? timeStamp : item.reservation_time;
            }
        }, "" as string);

        const timeToExpire = Number(process.env.REACT_APP_SHOPPING_CART_TIMEOUT_SECONDS) || 600;

        const newExpirationTime = latestReservationTime !== "" ? moment(latestReservationTime).add(timeToExpire, 'seconds') : undefined;
        const timeRemaining = newExpirationTime?.diff(moment(), 'seconds');
        if(timeRemaining === undefined)
        {
            stopReservationTimer();
        }
        timeRemaining && startReservationTimer(timeRemaining);
    },[props.lineItems])
    
    // Handler triggered when any line item reports a price change in order to adjust the price map
    const onPriceChange = (lineItemId: number, newAmount: CurrencyAmount) =>
    {
        setPriceMap(oldMap => 
        {
            return{...oldMap, [lineItemId]: newAmount}
        });
        //TODO: There may be different currencies in the future...
    }

    //Temporary Function to remove Shirts if Badges are Removed from cart.
    const validateCart = (items: LineItem[]) : void =>
    {
        const badgePresent = items.some((item) => {
            if (item.itemFormat !== LineItemTypes.ForSale)
            {
                return false;
            }
            if ((item.itemConfiguration as ForSaleConfiguration).quantity === 0)
            {
                return false;
            }
            return true;
        });

        const shirtPresent = items.some((item) => {
            if (item.itemFormat !== LineItemTypes.Shirts)
            {
                return false;
            }
            return true;
        });

        if (shirtPresent && !badgePresent)
        {
            const shirtItem = items.filter((item) =>
            {
                return item.itemFormat === LineItemTypes.Shirts;
            });

            shirtItem.map((item) => {
                onRemoveLineitem(item.itemConfiguration.dbId);
            });

            deleteCartId();
        }
    }

    const onRemoveLineitem = (lineItemDBId: number) : void =>
    {
        removeFromCart(lineItemDBId);

        // remove the item from the array of lineitems
        const tempItems = props.lineItems.filter(LineItem => LineItem.itemConfiguration.dbId !== lineItemDBId);

        // rebuild these maps from new array
        setPriceMap(constructDefaultPriceMap(tempItems));
        setValidityMap(constructValidityMap(tempItems));
        setDataMap(constructDataMap(tempItems));

        validateCart(tempItems);
    }


    // Handler triggered when any line item reportes a change in the price validity. An invalid price should cause no total price to be displayed
    // and should not allow payment to proceed. This is separate from onPrice Change because price validity should be based not only on the 
    // explit value, but also whether or not the user may still be editing the price, something which is controlled by the unit price type component.
    const onPriceValidityChange = (lineItemId: number, isValid: boolean, errorMsg?: string) =>
    {
        setValidityMap(oldMap => {
            return {...oldMap, [lineItemId] : 
            {
                priceValid: isValid, 
                dataValid: oldMap[lineItemId].dataValid, 
                priceErrMsg: errorMsg,
                dataErrMsg: oldMap[lineItemId].dataErrMsg,
            }}
        });
    }

    // Handler triggered when any line item reports a data change to adjust the data map. Simultaneously adjusts the data validity map.
    // A data invalid state should prevent payments from proceeding.
    const onDataChange = (lineItemId: number, newData: any, isValid: boolean, errorMsg?: string) =>
    {
        setDataMap(oldMap => {
            return {...oldMap, [lineItemId]: newData}
        });
        setValidityMap(oldMap => {
            return {...oldMap, [lineItemId] : 
            { 
                priceValid: oldMap[lineItemId].priceValid, 
                dataValid: isValid, 
                priceErrMsg: oldMap[lineItemId].priceErrMsg,
                dataErrMsg: errorMsg
            }}
        });
        
    }

    /**
     * Function that, given a price map, reports the grand total of the items in the map.
     * If any item has an invalid price, the value is returned as '$-.--'
     * TODO: The 'invalid display' should be based on the currencies provided, and not hardcoded as USD.
     * TODO: This assumes all currencies are the same. Will need to map out behavior when
     * currencies are different in the future.
     * @param priceMap  Map of Prices to use.
     * @hiddenParam validityMap: Map of validities to use. TODO: make this a direct parameter.
     * @returns FormatCurrencyValue providing the formatted, string, and float values of the total, as described above
     */
    const getTotalofPriceMap = (priceMap: Record<string, CurrencyAmount>) : FormatCurrencyValue => {

        //Check Price Validities
        if(!Object.entries(validityMap).reduce((acc, [name, validities]) => {
            return(acc && validities.priceValid)
        }, true))
        {
            return({formatted: '$-.--', float: 0.00, string: '-'});
        }

        //TODO: We are assuming for this calculation that all lineItems share the same currency.
        if(Object.values(priceMap).length === 0)
        {
            return({
                string: '-.--',
                formatted: '$-.--',
                float: 0
            })
        }

        const currency = Object.values(priceMap)[0].currency;

        const sumTotal = Object.values(priceMap)
        .map((amount) => amount.value)
        .reduce(((acc, value) => 
        {
            return(acc+((value?.float !== undefined) ? value.float : 0))
        }),0);

        return(formatCurrencyValue(sumTotal, currency.formatting));
    }

    /**
     * Function that renders the Line Item Display for each line item
     * @param lineItems an array of line items to display
     * @hiddenparam onPriceChange, onDataChange, and onPriceValidityChange are shared between all line items.
     * @returns Rendered LineItemDisplays for each line item.
     */
    const LineItemList = (lineItems: LineItem[]) => 
    {
        if (lineItems.length < 1)
        {
            return (NoItemsInList());
        }
        else 
        {
            return lineItems.sort((a, b) => a.displayOrd - b.displayOrd).map((lineItem) =>
            {
                return(
                    <div>
                        <LineItemDisplay
                        lineItem={lineItem}
                        onPriceChange={onPriceChange}
                        onDataChange={onDataChange}
                        onPriceValidityChange={onPriceValidityChange}
                        disableInputs={payRequestProcessing}
                        onRemoveFromCart={onRemoveLineitem}
                    />
                    <hr></hr>
                    </div>
                );
            })
        }
    }

    const NoItemsInList = () =>
    {
        return(<b>Your Shopping Cart is currently empty</b>)
    }

    /**
     * Effect for whenever validity map changes or Card Changes
     * to determine state of the pay button and messages to display.
     * TODO: Allow for lineItemTypes and unitPriceTypes to provide custom messages.
     */
    React.useEffect(() => 
    {
        const startingString = Object.entries(validityMap).reduce((acc, [name, validities]) => 
        {
            if(!validities.priceValid && validities.priceErrMsg)
            {
                acc.push(validities.priceErrMsg);
            }
            if(!validities.dataValid && validities.dataErrMsg)
            {
                acc.push(validities.dataErrMsg);
            }
            return acc;

        }, [] as string[]);

        if(cardErrors !== undefined && cardErrors.errorMsg.length !== 0)
        {
            startingString.push(...cardErrors.errorMsg);
        }

        const badgePresent = props.lineItems?.some((item) => {
            if (item.itemFormat !== LineItemTypes.ForSale)
            {
                return false;
            }
            const data = dataMap[item?.itemConfiguration.dbId ?? 0] as ForSaleData;
            const quantity = data?.registrationAttributions ? data.registrationAttributions.length : 0;
            if (quantity === 0)
            {
                return false;
            }
            return true;
        }) ?? false;

        const shirtPresent = props.lineItems?.some((item) => {
            if (item.itemFormat !== LineItemTypes.Shirts)
            {
                return false;
            }
            const data = dataMap[item?.itemConfiguration.dbId ?? 0] as ShirtData;
            const quantity = data.totalQuantity;
            if(quantity === 0)
            {
                return false;
            }
            return true;
        }) ?? false;

        if(shirtPresent && !badgePresent)
        {
            startingString.push("You must order a registration in order to order shirts.")
        }


        setValidationErrorArray(startingString);

    },[validityMap, cardErrors, dataMap, props.lineItems]);

    /**
     * Effect to update shopping cart when data on form has changed.
     * TODO: Remove props.lineItems Reliance.
     * With the new behavior, adding this to the dependency array causes a
     * re-render loop.
     * (Probably make it a reference of some sort.)
     */
    React.useEffect(() => 
    {
        if(props.lineItems)
        {
            for (const item of props.lineItems)
            {
                updateShoppingCartPrice(item.itemConfiguration.dbId);
                updateShoppingCartData(item.itemConfiguration.dbId);
            }
        }
    }, [validityMap, priceMap, dataMap]) //props.lineItems

    /**
     * Function called when a possible update to the shopping cart price may be necessary for a line item.
     * @param lineItemName The line item to update
     */
   const updateShoppingCartPrice = (lineItemId: number) => 
   {
        if(validityMap[lineItemId].priceValid === true)
        {
            const lineItemToUpdate = props.lineItems?.find((item) => item.itemConfiguration.dbId === lineItemId);
            lineItemToUpdate && updateToCart({
                ...lineItemToUpdate,
                defaultPriceDisplay: priceMap[lineItemId].value.formatted,
                unitPriceConfiguration: {
                    ...lineItemToUpdate.unitPriceConfiguration,
                    defaultPrice: priceMap[lineItemId]
                }
            });
        }
   }

    /**
     * Function called when a possible update to the shopping cart price may be necessary for a line item.
     * @param lineItemName The line item to update
     */
    const updateShoppingCartData = (lineItemId: number) => 
    {
        const lineItemToUpdate = props.lineItems?.find((item) => item.itemConfiguration.dbId === lineItemId);
        const data = dataMap[lineItemId];
        

        if(lineItemToUpdate === undefined)
        {
            return
        }

        if(lineItemToUpdate.itemFormat === LineItemTypes.ForSale)
        {
            const quantity = data?.registrationAttributions?.length ?? 0;
            console.log(data);
            lineItemToUpdate && updateToCart({
                ...lineItemToUpdate,
                itemConfiguration: {
                ...lineItemToUpdate.itemConfiguration,
                quantity: quantity
                } as ForSaleConfiguration,
                data: data
            });
        }
        if(lineItemToUpdate.itemFormat === LineItemTypes.Shirts)
        {
            lineItemToUpdate && updateToCart({
                ...lineItemToUpdate,
                data: data
            });
        }
        else
        {
            lineItemToUpdate && updateToCart({
                ...lineItemToUpdate,
                itemConfiguration: {
                ...lineItemToUpdate.itemConfiguration,
                },
                data: data
            });
        }
    }

    /**
     * Handler triggered when checkout button is pressed to generate payment request to backend.
     */

    const onPaymentSuccess = React.useCallback(() => {
        clearCheckoutEvent();
        clearCart();
        deleteCartId();
    },[]);

    /* TODO: Handle Failure better than this */
    const onPaymentFailure = React.useCallback(() => {
        clearCheckoutEvent();
        clearCart();
        deleteCartId();
    },[]);

    const onCheckoutSubmit = React.useCallback(async () => {
        stopReservationTimer();
        
        processPayment({ 
            priceMap: priceMap, 
            dataMap: dataMap, 
            lineItems: props.lineItems ?? [], 
            cardData: cardData, 
            onPaymentSuccess: onPaymentSuccess, 
            onPaymentFailure: onPaymentFailure 
        });
    }, [priceMap, dataMap, props.lineItems, cardData, onPaymentSuccess, onPaymentFailure]);

    const onCardChange = (card: CardInput) =>
    {
        setCardData(card);
    }

    const onCardValid = (isValid: boolean) =>
    {
        setCardValid(isValid);
    }

    const onCardErrorMessage = (inputState: CardInputState) =>
    {
        setCardErrors(inputState);
    }

    if (checkoutComplete)
    {
        return(
            <div>
                <CheckoutCompleted eventIds={checkoutEventIds} orderId={completedOrderId} order={completedOrder} />
            </div>
        )
    }

    return(
        <div className='checkoutPage'>

            <div className='checkoutLineItemList'>
                <b>Order Summary</b>
                {timerReservationStatus === IntervalTimerStatus.RUNNING && <span>   Your badges are reserved for {formattedReservationTimeRemaining} .</span>}
                <hr></hr>

                {props.lineItems ? LineItemList(props.lineItems) : NoItemsInList()}

            </div>

            {props.lineItems && <div className='totalBox'>

                <GACardInput
                    onCardChange={onCardChange}
                    onCardValid={onCardValid}
                    onErrorMessageChange={onCardErrorMessage}
                    disabled={payRequestProcessing}
                />

                <div className='checkoutPayButtonLine'>
                    <button className='checkoutPayButton'
                        disabled={!(props.lineItems && props.lineItems.length > 0) || (validationErrorArray.length !== 0) || !cardValid || payRequestProcessing}
                        onClick={onCheckoutSubmit}
                    >Pay</button>

                    <div className='grandTotal'>
                        <b> Total Cost: </b>
                        <b>{getTotalofPriceMap(priceMap).formatted}</b>
                    </div>
                </div>

                <div>
                    {validationErrorArray.map((error) => 
                    {
                        return(<p className='checkoutPageTextError'>{error}</p>)
                    })}
                </div>

                {payRequestProcessing && <PaymentProcessingWidget/>}
                {processingError && <div className='checkoutProcessingError'>
                    <b>Error</b>
                    <p>{processingError}</p>
                    {processingDelay && <span>{extendedProcessingMessage}</span>}
                </div>}

            </div>}

        </div>
    )
}

export default Checkout